import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { 
  Eye, 
  QrCode, 
  Clock, 
  CreditCard, 
  Download, 
  Share2,
  TrendingUp,
  ExternalLink
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'

export default function Overview() {
  const { currentUser, userProfile } = useAuth()
  const [vcard, setVcard] = useState(null)
  const [analytics, setAnalytics] = useState({ visits: 0, qrScans: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      if (!currentUser) return

      try {
        // Fetch vCard data
        const vcardDoc = await getDoc(doc(db, 'vcards', currentUser.uid))
        if (vcardDoc.exists()) {
          setVcard(vcardDoc.data())
        }

        // Fetch analytics
        const analyticsDoc = await getDoc(doc(db, 'analytics', currentUser.uid))
        if (analyticsDoc.exists()) {
          setAnalytics(analyticsDoc.data())
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [currentUser])

  const stats = [
    { 
      icon: <Eye size={22} />, 
      label: 'Total Views', 
      value: analytics.visits || 0,
      change: '+12%',
      positive: true 
    },
    { 
      icon: <QrCode size={22} />, 
      label: 'QR Scans', 
      value: analytics.qrScans || 0,
      change: '+8%',
      positive: true 
    },
    { 
      icon: <Clock size={22} />, 
      label: 'Last Visited', 
      value: analytics.lastVisited ? 'Today' : 'N/A',
      change: null 
    }
  ]

  const actions = [
    { 
      icon: <CreditCard size={24} />, 
      label: 'Edit vCard', 
      path: '/dashboard/edit' 
    },
    { 
      icon: <Download size={24} />, 
      label: 'Download QR', 
      path: '/dashboard/qr' 
    },
    { 
      icon: <Share2 size={24} />, 
      label: 'Share Link', 
      path: '/dashboard/qr' 
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '50vh' }}>
        <div className="spinner"></div>
      </div>
    )
  }

  return (
    <div className="overview-page">
      <div className="page-header">
        <h1 className="page-title">
          Welcome back, {userProfile?.name?.split(' ')[0] || 'User'}! 👋
        </h1>
        <p className="page-subtitle">
          Here's what's happening with your digital vCard
        </p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon">{stat.icon}</div>
              {stat.change && (
                <span className={`stat-change ${stat.positive ? 'positive' : 'negative'}`}>
                  <TrendingUp size={14} />
                  {stat.change}
                </span>
              )}
            </div>
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="dashboard-card mb-lg">
        <h3 className="dashboard-card-title">Quick Actions</h3>
        <div className="actions-grid">
          {actions.map((action, index) => (
            <Link key={index} to={action.path} className="action-card">
              <div className="action-icon">{action.icon}</div>
              <span className="action-label">{action.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* vCard Preview / CTA */}
      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3 className="dashboard-card-title">Your vCard</h3>
          {vcard ? (
            <div className="vcard-mini-preview">
              <div className="vcard-mini-header">
                <div 
                  className="vcard-mini-avatar"
                  style={{ 
                    backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {!vcard.profileImage && vcard.name?.[0]}
                </div>
                <div>
                  <h4>{vcard.name || 'Your Name'}</h4>
                  <p>{vcard.title || 'Your Title'}</p>
                </div>
              </div>
              <div className="vcard-mini-actions">
                <Link to="/dashboard/edit" className="btn btn-primary btn-sm">
                  Edit vCard
                </Link>
                <a 
                  href={`/v/${vcard.username}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm"
                >
                  View Live <ExternalLink size={14} />
                </a>
              </div>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 'var(--space-xl) 0' }}>
              <CreditCard size={40} style={{ color: 'var(--gray-400)', marginBottom: 'var(--space-md)' }} />
              <h4 style={{ marginBottom: 'var(--space-sm)' }}>No vCard Yet</h4>
              <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--space-md)' }}>
                Create your digital vCard to get started
              </p>
              <Link to="/dashboard/edit" className="btn btn-primary btn-sm">
                Create vCard
              </Link>
            </div>
          )}
        </div>

        <div className="dashboard-card">
          <h3 className="dashboard-card-title">Your Plan</h3>
          <div className="plan-info">
            <div className="plan-badge">
              {userProfile?.plan === 'free' ? (
                <span className="badge badge-secondary">Free Plan</span>
              ) : (
                <span className="badge badge-primary">Pro Plan</span>
              )}
            </div>
            {userProfile?.plan === 'free' ? (
              <>
                <p style={{ color: 'var(--text-muted)', marginTop: 'var(--space-md)', fontSize: 'var(--font-size-sm)' }}>
                  Upgrade to Pro for unlimited themes, advanced analytics, and more.
                </p>
                <Link to="/dashboard/billing" className="btn btn-primary btn-sm" style={{ marginTop: 'var(--space-md)' }}>
                  Upgrade to Pro - ₹99/year
                </Link>
              </>
            ) : (
              <>
                <p style={{ color: 'var(--text-muted)', marginTop: 'var(--space-md)', fontSize: 'var(--font-size-sm)' }}>
                  You have access to all premium features.
                </p>
                {userProfile?.expiresAt && (
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-sm)' }}>
                    Renews on {new Date(userProfile.expiresAt.toDate()).toLocaleDateString()}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .mb-lg { margin-bottom: var(--space-lg); }
        
        .vcard-mini-preview {
          padding: var(--space-md) 0;
        }
        
        .vcard-mini-header {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          margin-bottom: var(--space-lg);
        }
        
        .vcard-mini-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--font-size-xl);
          font-weight: 600;
        }
        
        .vcard-mini-header h4 {
          font-size: var(--font-size-base);
          font-weight: 600;
          margin-bottom: 2px;
        }
        
        .vcard-mini-header p {
          font-size: var(--font-size-sm);
          color: var(--text-muted);
        }
        
        .vcard-mini-actions {
          display: flex;
          gap: var(--space-sm);
        }
        
        .plan-info {
          padding: var(--space-md) 0;
        }
        
        .badge-secondary {
          background: var(--gray-100);
          color: var(--text-body);
        }
      `}</style>
    </div>
  )
}
